import { useEffect, useState } from 'preact/hooks';
import { fetchReviewAudio } from '../lib/api';
import './ReviewAudio.css';

// B7: the review's recorded audio, played through the OBJECT-URL pattern (the
// bytes ride the X-Tenant-Id header, which a bare <audio> src can't carry — same
// constraint as MediaEl/fetchSourceOriginal). Loaded LAZILY on the reviewer's
// tap, not on mount: a text-only review has no audio (the endpoint 404s), and we
// never want a wall of speculative 404s for a list of feedback cards. On failure
// it degrades to a calm "text note only", never a broken control.
export interface ReviewAudioProps {
  tenantId: string;
  reviewId: string;
  /** When true the player is present from the start (the caller already knows
   * audio exists — AuditionDetail's reviews carry `audioFileUrl`). When false/
   * omitted it starts as a lazy "play" button (Development's feedback, which
   * doesn't flag audio presence). */
  eager?: boolean;
}

type State = 'idle' | 'loading' | 'ready' | 'none';

export function ReviewAudio({ tenantId, reviewId, eager }: ReviewAudioProps) {
  const [state, setState] = useState<State>(eager ? 'loading' : 'idle');
  const [url, setUrl] = useState<string | null>(null);

  async function load() {
    setState('loading');
    try {
      const file = await fetchReviewAudio(tenantId, reviewId);
      setUrl(file.objectUrl);
      setState('ready');
    } catch {
      setState('none');
    }
  }

  useEffect(() => {
    if (eager) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, reviewId]);

  // Revoke the object URL on unmount / when it changes — no leaks.
  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [url]);

  if (state === 'ready' && url) {
    return (
      // eslint-disable-next-line jsx-a11y/media-has-caption -- recorded coach
      // feedback has no caption track; the aria-label names it.
      <audio class="rev-audio__el" src={url} controls preload="metadata" aria-label="Recorded feedback" />
    );
  }
  if (state === 'none') {
    return <div class="rev-audio__none">Text note only.</div>;
  }
  return (
    <button type="button" class="rev-audio__play" onClick={() => void load()} disabled={state === 'loading'}>
      <span aria-hidden="true">▶</span> {state === 'loading' ? 'Loading…' : 'Play audio note'}
    </button>
  );
}
